import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { TIME_SLOTS } from './constants';

const DS_COLS = [
  {k:'timeSlot',l:'TIME',w:140,t:'sel',o:TIME_SLOTS},
  {k:'dailySchedule',l:'DAILY SCHEDULE',w:260,t:'text'},
  {k:'dailyWork',l:'DAILY WORK',w:360,t:'text'},
];

const TH_NAME  = {background:'#000',color:'#fff',padding:'7px 10px',fontWeight:700,fontSize:11,textAlign:'center',border:'1px solid #333',minWidth:150,verticalAlign:'middle'};
const TH_YEL   = {background:'#FFD700',color:'#000',padding:'7px 10px',fontWeight:700,fontSize:11,textAlign:'center',border:'1px solid #cca200',verticalAlign:'middle'};
const TH_PCH   = {background:'#F4B183',color:'#000',padding:'7px 10px',fontWeight:700,fontSize:11,textAlign:'center',border:'1px solid #c87941',whiteSpace:'nowrap',verticalAlign:'middle'};
const TH_WHT   = {background:'#fff',color:'#555',padding:'7px 10px',fontWeight:400,fontSize:11,textAlign:'center',border:'1px solid #ddd',verticalAlign:'middle'};
const TH_DEL   = {background:'#ddd',color:'#333',padding:'7px 8px',fontWeight:700,fontSize:10,textAlign:'center',border:'1px solid #bbb',minWidth:44,position:'sticky',right:0};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const parseDateToISO = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const m = str.match(/^(\d{1,2})[.\-\/](\d{1,2})[.\-\/](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`;
  return null;
};

const getDatesInMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const days = new Date(y, m, 0).getDate();
  return Array.from({length: days}, (_, i) => `${ym}-${String(i+1).padStart(2,'0')}`);
};

const DailyScheduleTable = ({
  entries, onSaveRow, onDeleteRow, onAddRow, onAddDay,
  personName, isHR = false, saveAllTrigger = 0, onDirtyCountChange,
}) => {
  const [localEdits, setLocalEdits] = useState({});
  const [editingCell, setEditingCell] = useState({ rowId: null, colKey: null });
  const [editingGroupDate, setEditingGroupDate] = useState(null);
  const [addingDay, setAddingDay] = useState(false);
  const [newDayDate, setNewDayDate] = useState('');
  const saveTimerRef = useRef({});
  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const syncingRef = useRef(false);
  const prevTriggerRef = useRef(saveAllTrigger);

  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  const [topScrollWidth, setTopScrollWidth] = useState(0);

  // Auto-navigate to most recent month with data when current month has no entries
  useEffect(() => {
    if (entries.length === 0) return;
    const monthsWithData = new Set();
    entries.forEach(e => {
      const iso = parseDateToISO(e.row_data?.date || '') || e.row_data?.date || '';
      if (iso && iso.length >= 7) monthsWithData.add(iso.slice(0, 7));
    });
    if (monthsWithData.size === 0) return;
    // Only navigate if the current month has no data
    if (monthsWithData.has(currentMonth)) return;
    // Navigate to most recent month that has data
    const sorted = Array.from(monthsWithData).sort().reverse();
    if (sorted[0]) setCurrentMonth(sorted[0]);
  }, [entries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update top scrollbar width
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const update = () => setTopScrollWidth(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [entries, currentMonth]);

  const handleTopScroll = () => {
    if (syncingRef.current || !tableContainerRef.current) return;
    syncingRef.current = true;
    tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    syncingRef.current = false;
  };
  const handleTableScroll = () => {
    if (syncingRef.current || !topScrollRef.current) return;
    syncingRef.current = true;
    topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    syncingRef.current = false;
  };

  useEffect(() => {
    onDirtyCountChange?.(Object.keys(localEdits).length);
  }, [localEdits]); // eslint-disable-line

  useEffect(() => {
    if (prevTriggerRef.current === saveAllTrigger) return;
    prevTriggerRef.current = saveAllTrigger;
    saveAllDirty();
  }, [saveAllTrigger]); // eslint-disable-line

  // Build full month groups
  const allMonthGroups = useMemo(() => {
    const allDates = getDatesInMonth(currentMonth);

    // Build lookup by date+slot
    const existingByDateSlot = {};
    entries.forEach(e => {
      const date = parseDateToISO(e.row_data?.date||'') || e.row_data?.date || '';
      const slot = e.row_data?.timeSlot || '';
      existingByDateSlot[`${date}::${slot}`] = e;
    });

    const groups = [];

    // Current month: all dates × all TIME_SLOTS
    allDates.forEach(dateISO => {
      const dateSlots = [];
      TIME_SLOTS.forEach((slot, slotIdx) => {
        const key = `${dateISO}::${slot}`;
        if (existingByDateSlot[key]) {
          dateSlots.push(existingByDateSlot[key]);
        } else {
          dateSlots.push({
            id: `__virtual_${dateISO}_${slotIdx}__`,
            _isVirtual: true,
            row_data: { date: dateISO, timeSlot: slot, dailySchedule: '', dailyWork: '' },
          });
        }
      });
      // Also include existing entries with non-standard slots for this date
      entries.forEach(e => {
        const entryDate = parseDateToISO(e.row_data?.date||'') || e.row_data?.date || '';
        if (entryDate === dateISO && !TIME_SLOTS.includes(e.row_data?.timeSlot||'') && !dateSlots.find(s => s.id === e.id)) {
          dateSlots.push(e);
        }
      });
      groups.push({ date: dateISO, entries: dateSlots, _otherMonth: false });
    });

    // Other months entries
    const otherEntries = entries.filter(e => {
      const date = parseDateToISO(e.row_data?.date||'') || e.row_data?.date || '';
      return !allDates.includes(date);
    });
    if (otherEntries.length) {
      const otherGrouped = {};
      otherEntries.forEach(e => {
        const date = parseDateToISO(e.row_data?.date||'') || e.row_data?.date || '';
        if (!otherGrouped[date]) otherGrouped[date] = [];
        otherGrouped[date].push(e);
      });
      // Sort by date desc
      Object.keys(otherGrouped).sort().forEach(date => {
        groups.push({ date, entries: otherGrouped[date], _otherMonth: true });
      });
    }

    return groups;
  }, [entries, currentMonth]);

  const getV = (rowId, k, rd) => localEdits[rowId]?.[k] !== undefined ? localEdits[rowId][k] : (rd[k]??'');
  const handleChange = (rowId, k, val) => setLocalEdits(prev => ({...prev, [rowId]: {...(prev[rowId]||{}), [k]: val}}));

  const handleBlur = (rowId, entry) => {
    setEditingCell({ rowId: null, colKey: null });
    if (!localEdits[rowId]) return;
    if (saveTimerRef.current[rowId]) clearTimeout(saveTimerRef.current[rowId]);
    saveTimerRef.current[rowId] = setTimeout(() => {
      if (entry) {
        const updated = {...entry.row_data, ...localEdits[rowId]};
        if (entry._isVirtual) {
          // Only save if there's meaningful content
          const hasContent = Object.entries(localEdits[rowId]).some(([k, v]) =>
            !['date','timeSlot'].includes(k) && v && String(v).trim() !== ''
          );
          if (hasContent) onAddRow(updated);
        } else {
          onSaveRow(rowId, updated);
        }
      }
      setLocalEdits(prev => { const n={...prev}; delete n[rowId]; return n; });
    }, 400);
  };

  const saveAllDirty = async () => {
    Object.values(saveTimerRef.current).forEach(t => clearTimeout(t));
    saveTimerRef.current = {};
    setEditingCell({ rowId: null, colKey: null });
    const editsSnapshot = { ...localEdits };
    setLocalEdits({});
    for (const [rowId, edits] of Object.entries(editsSnapshot)) {
      let found = null;
      for (const g of allMonthGroups) {
        const e = g.entries.find(e => e.id === rowId);
        if (e) { found = e; break; }
      }
      if (!found) continue;
      const updated = { ...found.row_data, ...edits };
      if (found._isVirtual) {
        const hasContent = Object.entries(edits).some(([k, v]) =>
          !['date','timeSlot'].includes(k) && v && String(v).trim() !== ''
        );
        if (hasContent) await onAddRow(updated);
      } else {
        await onSaveRow(rowId, updated);
      }
    }
  };

  const handleGroupDateSave = (origDate, newDate) => {
    if (!newDate || newDate === origDate) { setEditingGroupDate(null); return; }
    entries.filter(e => {
      const d = parseDateToISO(e.row_data?.date||'') || e.row_data?.date || '';
      return d === origDate;
    }).forEach(entry => {
      onSaveRow(entry.id, {...entry.row_data, date: newDate});
    });
    setEditingGroupDate(null);
  };

  const prevMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m-2, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };
  const nextMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };
  const [ymY, ymM] = currentMonth.split('-').map(Number);
  const monthLabel = `${MONTH_NAMES[ymM-1]} ${ymY}`;

  const renderCell = (rowId, col, rd, entry) => {
    const val = getV(rowId, col.k, rd);
    const isEdit = editingCell.rowId === rowId && editingCell.colKey === col.k;
    const isVirtual = !!entry?._isVirtual;
    const bg = col.k==='dailyWork' ? (isVirtual ? '#B8ECC7' : '#00B050') : (isVirtual ? '#F0F4FF' : '#fff');
    const fg = col.k==='dailyWork' ? (isVirtual ? '#1e4d2b' : '#fff') : '#333';
    return (
      <td key={col.k} style={{minWidth:col.w, background:bg, border:'1px solid #d1d5db', padding:0, cursor:'cell'}}
        onClick={() => setEditingCell({ rowId, colKey: col.k })}>
        {isEdit ? (
          col.t==='sel' ? (
            <select autoFocus value={val} onChange={e=>handleChange(rowId,col.k,e.target.value)}
              onBlur={()=>handleBlur(rowId, entry)}
              onKeyDown={e=>{if(e.key==='Escape'||e.key==='Enter'){e.preventDefault();setEditingCell({rowId:null,colKey:null});}}}
              className="w-full h-full px-1.5 py-1 text-[11px] border-0 outline-none bg-sky-50 ring-2 ring-inset ring-sky-500" style={{minWidth:col.w}}>
              {(col.o||[]).map(o=><option key={o} value={o}>{o||'—'}</option>)}
            </select>
          ) : (
            <input autoFocus type="text" value={val} onChange={e=>handleChange(rowId,col.k,e.target.value.toUpperCase())}
              onBlur={()=>handleBlur(rowId, entry)}
              onKeyDown={e=>{if(e.key==='Escape'||e.key==='Enter'){e.preventDefault();setEditingCell({rowId:null,colKey:null});}}}
              className="w-full h-full px-1.5 py-1 text-[11px] border-0 outline-none bg-sky-50 ring-2 ring-inset ring-sky-500"
              style={{minWidth:col.w, textTransform:'uppercase'}} />
          )
        ) : (
          <div className="px-2 py-1.5 min-h-[28px]" style={{color:fg}}>
            <span className="text-[11px] leading-tight">{val || <span className="text-slate-300 text-[10px]">—</span>}</span>
          </div>
        )}
      </td>
    );
  };

  const renderGroup = (group) => {
    return group.entries.map((entry, entryIdx) => {
      const isFirstOfGroup = entryIdx === 0;
      const rowId = entry.id;
      const rd = entry.row_data || {};
      const isVirtual = !!entry._isVirtual;
      const isEditingDate = editingGroupDate?.origDate === group.date;
      const hasDirty = !!localEdits[rowId];

      return (
        <tr key={rowId} style={{background: hasDirty ? '#FFF9E6' : (isVirtual ? '#FAFBFF' : '#fff')}}>
          {isFirstOfGroup ? (
            <td
              rowSpan={group.entries.length}
              style={{
                minWidth:150, background: group._otherMonth ? '#FEF3C7' : '#F4B183',
                borderTop:'2px solid #c87941', borderBottom:'2px solid #c87941',
                borderLeft:'2px solid #c87941', borderRight:'1px solid #c87941',
                padding:0, cursor:'cell', verticalAlign:'middle', textAlign:'center',
              }}
              onClick={() => !isVirtual && setEditingGroupDate({ origDate: group.date, newDate: group.date })}
            >
              {isEditingDate ? (
                <input autoFocus type="date" defaultValue={editingGroupDate.newDate}
                  onChange={e => setEditingGroupDate(prev => ({...prev, newDate: e.target.value}))}
                  onBlur={e => handleGroupDateSave(group.date, e.target.value)}
                  onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();handleGroupDateSave(group.date,e.target.value);} if(e.key==='Escape'){setEditingGroupDate(null);} }}
                  className="w-full text-center px-1 py-2 text-[11px] border-0 outline-none bg-orange-100 text-slate-800 ring-2 ring-inset ring-orange-500" />
              ) : (
                <div className="px-2 py-2 text-[12px] font-bold text-slate-800 text-center leading-snug">
                  {group.date || '—'}
                </div>
              )}
            </td>
          ) : null}
          {DS_COLS.map(col => renderCell(rowId, col, rd, entry))}
          <td className="border border-slate-200 px-1 py-1 text-center sticky right-0 bg-white" style={{minWidth:44}}>
            {isVirtual ? (
              <span className="text-[10px] text-slate-300">—</span>
            ) : (
              <button
                onClick={() => {
                  if (!window.confirm('Clear this slot\'s data? The time slot will remain empty.')) return;
                  onSaveRow(entry.id, { date: entry.row_data.date, timeSlot: entry.row_data.timeSlot, dailySchedule: '', dailyWork: '' });
                }}
                className="p-1 rounded bg-red-400 text-white hover:bg-red-500" title="Clear slot data">
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </td>
        </tr>
      );
    });
  };

  return (
    <div className="flex flex-col">
      {/* Month navigation */}
      <div className="flex items-center gap-2 px-1 mb-1">
        <button onClick={prevMonth} className="p-1 rounded hover:bg-slate-100 text-slate-600" data-testid="ds-month-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs font-semibold text-slate-700 min-w-[120px] text-center">{monthLabel}</span>
        <button onClick={nextMonth} className="p-1 rounded hover:bg-slate-100 text-slate-600" data-testid="ds-month-next">
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="text-[11px] text-slate-400 ml-1">
          {getDatesInMonth(currentMonth).length} days × 12 time slots
        </span>
      </div>

      {/* Top scrollbar */}
      <div
        ref={topScrollRef}
        onScroll={handleTopScroll}
        style={{ overflowX: 'auto', overflowY: 'hidden', height: 16, borderRadius: 4, marginBottom: 2 }}
        data-testid="ds-top-scrollbar"
      >
        <div style={{ width: topScrollWidth || '100%', height: 1 }} />
      </div>

      <div
        ref={tableContainerRef}
        onScroll={handleTableScroll}
        className="overflow-auto border border-slate-200 rounded-lg"
        style={{maxHeight: 580}}
      >
        <table className="border-collapse text-xs" style={{width:'max-content',minWidth:'100%'}}>
          <thead style={{position:'sticky',top:0,zIndex:30}}>
            <tr>
              <th colSpan={2} style={{...TH_NAME,minWidth:290,fontSize:13,letterSpacing:'0.1em'}}>
                {(personName || 'EMPLOYEE').toUpperCase()}
              </th>
              <th style={{...TH_YEL,minWidth:260}}>DAILY SCHEDULE</th>
              <th style={{...TH_YEL,minWidth:360}}>DAILY WORK</th>
              <th style={TH_DEL}></th>
            </tr>
            <tr>
              <th style={{...TH_PCH,minWidth:150}}>DAY AND DATE</th>
              <th style={{...TH_PCH,minWidth:140}}>TIME</th>
              <th style={{...TH_WHT,minWidth:260,fontSize:10,color:'#888',fontStyle:'italic'}}>Weekly plan (Saturday)</th>
              <th style={{...TH_WHT,minWidth:360,fontSize:10,color:'#888',fontStyle:'italic'}}>Daily hourly work updates</th>
              <th style={TH_DEL}>DEL</th>
            </tr>
          </thead>
          <tbody>
            {allMonthGroups.map((group, gIdx) => {
              // Separator between current and other months
              if (gIdx > 0 && group._otherMonth && !allMonthGroups[gIdx-1]._otherMonth) {
                return [
                  <tr key="other-sep">
                    <td colSpan={5} style={{padding:'4px 12px', background:'#F1F5F9', color:'#64748b', fontSize:10, fontWeight:600, borderTop:'2px dashed #CBD5E1'}}>
                      DATA FROM OTHER MONTHS
                    </td>
                  </tr>,
                  ...renderGroup(group),
                ];
              }
              return renderGroup(group);
            })}
          </tbody>
        </table>
      </div>

      {!isHR && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {addingDay ? (
            <div className="flex items-center gap-2 bg-orange-50 border border-orange-300 rounded-lg px-3 py-2">
              <span className="text-xs font-semibold text-orange-700">Select Date:</span>
              <input type="date" value={newDayDate} onChange={e => setNewDayDate(e.target.value)}
                className="text-xs border border-orange-300 rounded px-2 py-1 text-slate-700 outline-none focus:ring-2 focus:ring-orange-400" />
              <button
                onClick={async () => { await onAddDay(newDayDate); setAddingDay(false); setNewDayDate(''); }}
                disabled={!newDayDate}
                className="text-xs bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 text-white font-bold px-3 py-1 rounded transition-colors">
                Add 12 Slots
              </button>
              <button onClick={() => { setAddingDay(false); setNewDayDate(''); }}
                className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1">Cancel</button>
            </div>
          ) : (
            <button
              onClick={() => setAddingDay(true)}
              data-testid="add-report-row-btn"
              className="flex items-center gap-1.5 text-xs bg-orange-500 hover:bg-orange-600 text-white font-semibold px-3 py-1.5 rounded-lg transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add Extra Day
            </button>
          )}
          <span className="text-xs text-slate-400 italic">Full month shown — click any cell to edit &bull; Click date to edit all slots for that day</span>
        </div>
      )}
    </div>
  );
};

export default DailyScheduleTable;
